/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useContext, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'

import { EuiCallOut, EuiFieldText, EuiFlexGroup, EuiForm, EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import LandingPageOuterContainer from '@modules/access-management-components/LandingPageOuterContainer'
import ContactSupport from '@modules/access-management-components/ContactSupport'
import LandingPageInnerContainer from '@modules/access-management-components/LandingPageInnerContainer'
import { useAnswersState } from '@modules/discovery-questions-lib/hooks'
import ProfileContext from '@modules/profile-lib/ProfileContext'
import { useCreateOrganizationMutation } from '@modules/profile-lib/hooks'
import externalNavigate from '@modules/routing/externalNavigate'
import { generateConnectAutoOpsWizardUrl } from '@modules/autoops-lib/util'

// eslint-disable-next-line import/no-restricted-paths
import SpinButton from '@/components/SpinButton'

import { usePersistOnboardingQuestions } from './hooks/use-persist-onboarding-question'
import { useRedirectToHome } from './hooks/use-redirect-to-home'

const messages = defineMessages({
  fullName: {
    id: 'ccm-onboarding-page.full-name',
    defaultMessage: 'Full name',
  },
  company: {
    id: 'ccm-onboarding-page.company',
    defaultMessage: 'Company',
  },
})

const CreateOrgForCCMPage = () => {
  const { formatMessage } = useIntl()
  const { answers, setAnswer } = useAnswersState({ initialState: {} })
  const { full_name: fullName, company } = answers

  const [isLaunching, setIsLaunching] = useState(false)

  const profileQuery = useContext(ProfileContext)
  const userHasOrg = profileQuery.isReady && profileQuery.user?.organization_id

  const { mutate: createOrgMutate } = useCreateOrganizationMutation({})

  const [error, setError] = useState<React.ReactNode>()

  const { putDiscoveryQuestions } = usePersistOnboardingQuestions({
    answers,
    setIsLaunchingState: setIsLaunching,
    setErrors: (errors) => {
      if (errors.length > 0) {
        setError(
          <FormattedMessage
            id='ccm-onboarding-page.error-message'
            defaultMessage='Sorry, we were unable to save your answers.'
          />,
        )
      }
    },
  })

  const navigateToConnectAutoOpsWizard = ({ organizationId }) => {
    externalNavigate(
      generateConnectAutoOpsWizardUrl({
        organizationId,
      }),
      { replace: true },
    )
  }

  useRedirectToHome()

  return (
    <LandingPageOuterContainer footer={<ContactSupport />}>
      <LandingPageInnerContainer
        title={
          <FormattedMessage id='create-org-for-ccm.title' defaultMessage="Let's get you set up!" />
        }
      >
        <EuiForm
          component='form'
          onSubmit={(e) => {
            setError(undefined)
            setIsLaunching(true)
            e.preventDefault()

            putDiscoveryQuestions(() => {
              !userHasOrg &&
                createOrgMutate(undefined, {
                  onSuccess: ({ id: organizationId }) => {
                    navigateToConnectAutoOpsWizard({ organizationId })
                    setIsLaunching(false)
                  },
                  onError: () => {
                    setError(
                      <FormattedMessage
                        id='alert.sorry-an-error-occurred'
                        defaultMessage='Sorry, looks like an error occurred.'
                      />,
                    )
                    setIsLaunching(false)
                  },
                })
            })
          }}
        >
          <EuiFlexGroup direction='column' gutterSize='m'>
            <EuiFieldText
              data-test-id='signup.discovery-questions.full-name'
              value={fullName || ''}
              placeholder={formatMessage(messages.fullName)}
              onChange={(e) => setAnswer({ full_name: e.target.value })}
              autoFocus={true}
            />

            <EuiFieldText
              data-test-id='signup.discovery-questions.company-name'
              value={company || ''}
              placeholder={formatMessage(messages.company)}
              onChange={(e) => setAnswer({ company: e.target.value })}
            />

            {error && (
              <EuiCallOut
                title={error}
                color='danger'
                iconType='error'
                onDismiss={() => setError(undefined)}
              />
            )}

            <SpinButton
              type='submit'
              disabled={!fullName || !company || isLaunching}
              fill={true}
              buttonProps={{ fullWidth: true }}
              spin={isLaunching}
              data-test-id='ccm-accept-and-continue-button'
            >
              <FormattedMessage
                id='cloud-signup.ccm-create-org-button-label'
                defaultMessage='Accept and continue'
              />
            </SpinButton>

            <EuiText size='xs' textAlign='center' color='subdued'>
              <i>
                <FormattedMessage
                  id='create-org-for-ccm.description'
                  defaultMessage={`By clicking (“Accept and Continue”) you confirm that you have read and understood the terms of the {ccmAddendum} you represent and warrant that you are duly authorized to accept such terms on behalf of your company.`}
                  values={{
                    ccmAddendum: (
                      <a
                        href='' // TODO: Add link
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        <FormattedMessage
                          id='create-org-for-ccm.ccm-addendum'
                          defaultMessage='Cloud Connected Mode Addendum'
                        />
                      </a>
                    ),
                  }}
                />
              </i>
            </EuiText>
          </EuiFlexGroup>
        </EuiForm>
      </LandingPageInnerContainer>
    </LandingPageOuterContainer>
  )
}

export default CreateOrgForCCMPage
